import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Mic, ShieldCheck, ShieldAlert } from 'lucide-react';
import clsx from 'clsx';
import { apiClient, ApiError } from '@/lib/api';
import type { components } from '@/lib/generated/types';
import { Card } from '@/components/ui/card';
import { ErrorState, LoadingState } from '@/components/ui/empty-state';
import {
  type AsrRealtimeScenario,
  type RealtimeOrderEditorState,
  buildRealtimeOrderPatch,
  includedRealtimeOrder,
  isRealtimeOptionSelectable,
  moveRealtimeRow,
  realtimeEditorsEqual,
  realtimeProviderShortLabel,
  resolveRealtimeOrderEditor,
  toggleRealtimeRow,
} from '@/lib/asr-realtime-settings';
import {
  missingRecordingFileCredHints,
  noRecordingFileProviderConfigured,
} from '@/lib/asr-file-provider-status';

/**
 * Issue #139 — 管理员「系统设置 → 语音识别」provider 开关 + 健康检查。
 *
 * 全部数据来自后端 GET /admin/asr/settings（已脱敏：只含 provider id、可用性布尔
 * 与环境变量**名称**提示，绝无密钥值）。切换主用 provider 通过 PATCH 写入并落审计。
 * 仅 ADMIN/SUPER_ADMIN 可编辑（后端 RBAC 双重拦截）；其余角色只读。
 */

interface AsrProviderHealth {
  id: string;
  label: string;
  aliases: string[];
  configured: boolean;
  configHint: string;
  // Issue #258 / #265：file = 录音文件批量 ASR；realtime = 实时语音流 ASR。
  kind?: 'file' | 'realtime';
  // Issue #265：实时 provider 是否已接入流式实现（false = 仅配置可见）。
  streamable?: boolean;
}

// Issue #367（PR #368 返修）— 直接派生自 OpenAPI 生成类型（apps/api/openapi.yaml →
// openapi-typescript），含后端恒返回的 kind: 'file' | 'realtime'。禁止在此手写字段定义，
// 使 scenarios[].kind 等契约锁死在 SSOT，杜绝管理端本地类型与后端响应 / 生成客户端漂移。
type AsrScenarioSetting = components['schemas']['AsrScenarioSetting'];

interface AsrProviderSettings {
  primary: string | null;
  fallbackOrder: string[];
  scenarios: Record<string, string[] | null>;
  languages?: Record<AsrScenarioSetting['scenario'], string>;
  updatedAt?: string;
  updatedBy?: string;
}

interface AsrSettingsView {
  settings: AsrProviderSettings;
  providers: AsrProviderHealth[];
  // Issue #265：实时语音流 ASR provider（与录音文件批量 providers 分列）。
  realtimeProviders?: AsrProviderHealth[];
  scenarios: AsrScenarioSetting[];
  languageOptions?: Array<{ value: string; label: string }>;
  envOrder: string | null;
  realtimeEnvOrder?: string | null;
  defaultOrder: string[];
  defaultRealtimeOrder?: string[];
  serviceAvailable: boolean;
}

const SCENARIO_LABELS: Record<AsrScenarioSetting['scenario'], string> = {
  sopFinal: 'SOP 培训最终转写',
  qcFinal: '质检最终转写',
  sopRealtime: 'SOP 培训实时转写',
  qcRealtime: '质检实时转写',
};

// Issue #357 — 两个独立实时场景，各自配置 1-2-3 兜底顺序（保存互不干扰、绝不动文件 ASR）。
const REALTIME_SCENARIOS: AsrRealtimeScenario[] = ['sopRealtime', 'qcRealtime'];

const REALTIME_SCENARIO_LABELS: Record<AsrRealtimeScenario, string> = {
  sopRealtime: 'SOP 培训实时识别',
  qcRealtime: '质检中心实时识别',
};

const INHERIT = '__inherit__';

// Issue #367 — 判定某场景走录音文件批量链还是实时流式链。契约（OpenAPI AsrScenarioSetting.kind）
// 保证后端恒返回 kind；保留按场景 id（sopRealtime / qcRealtime 为实时，其余为文件）的兜底，仅为
// 兼容灰度期可能命中的旧后端响应，避免管理端把实时链误当文件主链路展示。
function scenarioKind(s: AsrScenarioSetting): 'file' | 'realtime' {
  if (s.kind) return s.kind;
  return s.scenario === 'sopRealtime' || s.scenario === 'qcRealtime' ? 'realtime' : 'file';
}

export function AsrProviderSettingsCard({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['admin', 'asr', 'settings'],
    queryFn: () => apiClient.get<AsrSettingsView>('/admin/asr/settings'),
  });

  const [primary, setPrimary] = useState<string>(INHERIT);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  // Issue #363（返修 P0-2）：实时语音转写**完整 1-2-3 兜底顺序**按场景独立配置（独立于文件 ASR 的
  // primary/fallbackOrder）。每个场景一份有序编辑器状态（inherit + 有序 rows）。
  const [realtimeEditors, setRealtimeEditors] = useState<
    Record<AsrRealtimeScenario, RealtimeOrderEditorState>
  >({
    sopRealtime: { inherit: true, rows: [] },
    qcRealtime: { inherit: true, rows: [] },
  });
  const [realtimeNote, setRealtimeNote] = useState<string | null>(null);
  const [realtimeSavingScenario, setRealtimeSavingScenario] =
    useState<AsrRealtimeScenario | null>(null);
  const [languages, setLanguages] = useState<Record<AsrScenarioSetting['scenario'], string>>({
    sopFinal: 'auto',
    qcFinal: 'auto',
    sopRealtime: 'auto',
    qcRealtime: 'auto',
  });
  const [languageNote, setLanguageNote] = useState<string | null>(null);

  // Sync the local selects with server state whenever it (re)loads.
  useEffect(() => {
    if (query.data) {
      setPrimary(query.data.settings.primary ?? INHERIT);
      const realtimeIds = (query.data.realtimeProviders ?? []).map((p) => p.id);
      setRealtimeEditors({
        sopRealtime: resolveRealtimeOrderEditor(
          query.data.settings.scenarios?.sopRealtime,
          realtimeIds,
        ),
        qcRealtime: resolveRealtimeOrderEditor(
          query.data.settings.scenarios?.qcRealtime,
          realtimeIds,
        ),
      });
      setLanguages({
        sopFinal: query.data.settings.languages?.sopFinal ?? 'auto',
        qcFinal: query.data.settings.languages?.qcFinal ?? 'auto',
        sopRealtime: query.data.settings.languages?.sopRealtime ?? 'auto',
        qcRealtime: query.data.settings.languages?.qcRealtime ?? 'auto',
      });
    }
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: (nextPrimary: string) => {
      // primary 决定顺序：主用置顶，其余 provider 作为兜底链；继承=清空回退到环境变量/默认。
      if (nextPrimary === INHERIT) {
        return apiClient.patch<AsrSettingsView>('/admin/asr/settings', {
          primary: null,
          fallbackOrder: [],
        });
      }
      const others = (query.data?.providers ?? [])
        .map((p) => p.id)
        .filter((id) => id !== nextPrimary);
      return apiClient.patch<AsrSettingsView>('/admin/asr/settings', {
        primary: nextPrimary,
        fallbackOrder: [nextPrimary, ...others],
      });
    },
    onSuccess: async () => {
      setSavedNote('已保存，新转写任务即时生效');
      await queryClient.invalidateQueries({ queryKey: ['admin', 'asr', 'settings'] });
    },
  });

  // Issue #363（返修 P0-2）：保存实时**完整有序兜底链**时**只**写目标 scenarios.<scenario>
  // （sopRealtime / qcRealtime，绝不污染文件 ASR 的 primary/fallbackOrder）；持久化精确有序数组，
  // 继承部署默认写 { scenarios: { [scenario]: null } }。
  const realtimeMutation = useMutation({
    mutationFn: (input: { scenario: AsrRealtimeScenario; editor: RealtimeOrderEditorState }) => {
      setRealtimeSavingScenario(input.scenario);
      return apiClient.patch<AsrSettingsView>(
        '/admin/asr/settings',
        buildRealtimeOrderPatch(input.editor, input.scenario),
      );
    },
    onSuccess: async () => {
      setRealtimeNote('已保存，新实时转写会话即时生效');
      await queryClient.invalidateQueries({ queryKey: ['admin', 'asr', 'settings'] });
    },
    onSettled: () => setRealtimeSavingScenario(null),
  });

  const languageMutation = useMutation({
    mutationFn: (next: Record<AsrScenarioSetting['scenario'], string>) =>
      apiClient.patch<AsrSettingsView>('/admin/asr/settings', { languages: next }),
    onSuccess: async () => {
      setLanguageNote('已保存，新 ASR 任务和新实时会话即时生效');
      await queryClient.invalidateQueries({ queryKey: ['admin', 'asr', 'settings'] });
    },
  });

  const view = query.data;
  const dirty = useMemo(
    () => !!view && primary !== (view.settings.primary ?? INHERIT),
    [view, primary],
  );
  const realtimeDirty = useMemo(() => {
    const realtimeIds = (view?.realtimeProviders ?? []).map((p) => p.id);
    const dirtyFor = (scenario: AsrRealtimeScenario) =>
      !!view &&
      !realtimeEditorsEqual(
        realtimeEditors[scenario],
        resolveRealtimeOrderEditor(view.settings.scenarios?.[scenario], realtimeIds),
      );
    return {
      sopRealtime: dirtyFor('sopRealtime'),
      qcRealtime: dirtyFor('qcRealtime'),
    };
  }, [view, realtimeEditors]);
  const languageDirty = useMemo(() => {
    if (!view) return false;
    const current = view.settings.languages ?? {
      sopFinal: 'auto',
      qcFinal: 'auto',
      sopRealtime: 'auto',
      qcRealtime: 'auto',
    };
    return (
      languages.sopFinal !== (current.sopFinal ?? 'auto') ||
      languages.qcFinal !== (current.qcFinal ?? 'auto') ||
      languages.sopRealtime !== (current.sopRealtime ?? 'auto') ||
      languages.qcRealtime !== (current.qcRealtime ?? 'auto')
    );
  }, [view, languages]);

  if (query.isLoading) {
    return (
      <Card title="语音识别 Provider">
        <LoadingState label="加载语音识别设置…" />
      </Card>
    );
  }
  if (query.isError || !view) {
    return (
      <Card title="语音识别 Provider">
        <ErrorState
          title="加载语音识别设置失败"
          description={
            query.error instanceof ApiError ? query.error.message : undefined
          }
        />
      </Card>
    );
  }

  return (
    <Card title="语音识别 Provider">
      <div className="flex items-center gap-2" data-testid="asr-service-availability">
        {view.serviceAvailable ? (
          <ShieldCheck className="h-5 w-5 text-emerald-500" />
        ) : (
          <ShieldAlert className="h-5 w-5 text-red-500" />
        )}
        <span
          className={clsx(
            'text-sm font-medium',
            view.serviceAvailable ? 'text-emerald-600' : 'text-red-600',
          )}
        >
          {view.serviceAvailable ? '语音识别服务可用' : '语音识别服务不可用（无已配置 provider）'}
        </span>
      </div>

      {/* Issue #270 — 录音文件 provider 全未配置 = 长音频退化到实时 IAT 切片（慢、易卡）的根因。
          显著提示管理员去配置缺失凭据（env 名来自各 provider 的脱敏 configHint）。 */}
      {noRecordingFileProviderConfigured(view.providers) ? (
        <div
          className="mt-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-800"
          data-testid="asr-no-file-provider-warning"
        >
          <div className="font-semibold">未配置任何「录音文件转写」provider</div>
          <p className="mt-1 text-xs leading-relaxed text-amber-700">
            长音频（本地上传 / 音频 URL）会退化到实时听写 IAT 的 ≤60s 切片推流，速度慢、长录音
            易卡在「转写中」。请在服务器为以下任一录音文件 provider 配置凭据后重试：
          </p>
          <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-xs text-amber-700">
            {missingRecordingFileCredHints(view.providers).map((hint) => (
              <li key={hint}>
                <code className="text-[11px]">{hint}</code>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mt-4 text-xs font-medium uppercase tracking-wide text-slate-500">
        录音文件转写（批量，录音文件 / 音频 URL）
      </div>
      <div className="mt-2 space-y-2">
        {view.providers.map((p) => (
          <div
            key={p.id}
            className="flex items-start justify-between rounded-lg border border-slate-200 px-3 py-2"
            data-testid={`asr-provider-${p.id}`}
          >
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
                <Mic className="h-4 w-4 text-slate-400" />
                {p.label}
                <code className="text-xs text-slate-400">{p.id}</code>
              </div>
              <div className="mt-0.5 text-xs text-slate-500">{p.configHint}</div>
            </div>
            <span
              className={clsx(
                'shrink-0 rounded-full px-2 py-0.5 text-xs font-medium',
                p.configured
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-amber-50 text-amber-700',
              )}
            >
              {p.configured ? '已配置' : '未配置'}
            </span>
          </div>
        ))}
      </div>

      {view.realtimeProviders && view.realtimeProviders.length > 0 ? (
        <>
          <div className="mt-5 text-xs font-medium uppercase tracking-wide text-slate-500">
            实时语音转写（流式，SOP 培训 / 质检中心实时）
          </div>
          <div className="mt-2 space-y-2">
            {view.realtimeProviders.map((p) => (
              <div
                key={p.id}
                className="flex items-start justify-between rounded-lg border border-slate-200 px-3 py-2"
                data-testid={`asr-realtime-provider-${p.id}`}
              >
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
                    <Mic className="h-4 w-4 text-slate-400" />
                    {p.label}
                    <code className="text-xs text-slate-400">{p.id}</code>
                    {p.streamable === false ? (
                      <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
                        流式接入待后续
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500">{p.configHint}</div>
                </div>
                <span
                  className={clsx(
                    'shrink-0 rounded-full px-2 py-0.5 text-xs font-medium',
                    p.configured
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-amber-50 text-amber-700',
                  )}
                >
                  {p.configured ? '已配置' : '未配置'}
                </span>
              </div>
            ))}
          </div>

          {/* Issue #363（返修 P0-2）：实时语音转写**完整 1-2-3 兜底顺序**按场景独立配置（SOP 培训实时 /
              质检中心实时）。每个场景一个有序编辑器：勾选参与兜底的 provider、↑↓ 调整优先级，保存持久化
              **精确有序数组**到 scenarios.<scenario>，绝不动文件 ASR primary/fallbackOrder。默认继承部署默认。 */}
          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            {REALTIME_SCENARIOS.map((scenario) => {
              const editor = realtimeEditors[scenario];
              const included = includedRealtimeOrder(editor.rows);
              const saving = realtimeSavingScenario === scenario && realtimeMutation.isPending;
              const anyPending = realtimeMutation.isPending;
              const setEditor = (next: RealtimeOrderEditorState) => {
                setRealtimeEditors((prev) => ({ ...prev, [scenario]: next }));
                setRealtimeNote(null);
              };
              return (
                <div
                  key={scenario}
                  className="rounded-lg border border-slate-200 p-3"
                  data-testid={`asr-realtime-order-${scenario}`}
                >
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    {REALTIME_SCENARIO_LABELS[scenario]}
                  </div>

                  <label className="mt-2 flex items-center gap-2 text-xs text-slate-600">
                    <input
                      type="checkbox"
                      checked={editor.inherit}
                      disabled={!canEdit || anyPending}
                      data-testid={`asr-realtime-inherit-${scenario}`}
                      onChange={() => setEditor({ ...editor, inherit: !editor.inherit })}
                    />
                    继承部署默认（{(view.defaultRealtimeOrder ?? []).join(' → ') || '后端实时链'}）
                  </label>

                  {/* 有序兜底链编辑器：inherit 时禁用（展示但不参与保存）。 */}
                  <ol className="mt-2 space-y-1.5">
                    {editor.rows.map((row, i) => {
                      const provider = (view.realtimeProviders ?? []).find((p) => p.id === row.id);
                      const selectable = provider ? isRealtimeOptionSelectable(provider) : true;
                      const disabled = !canEdit || editor.inherit || anyPending;
                      const rank = included.indexOf(row.id);
                      return (
                        <li
                          key={row.id}
                          className={clsx(
                            'flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-sm',
                            editor.inherit
                              ? 'border-slate-100 bg-slate-50 text-slate-400'
                              : 'border-slate-200 text-slate-700',
                          )}
                          data-testid={`asr-realtime-row-${scenario}-${row.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={row.included}
                            disabled={disabled || !selectable}
                            data-testid={`asr-realtime-include-${scenario}-${row.id}`}
                            onChange={() => setEditor({ ...editor, rows: toggleRealtimeRow(editor.rows, i) })}
                          />
                          <span className="w-5 text-center font-mono text-xs text-slate-400">
                            {row.included && rank >= 0 ? rank + 1 : '·'}
                          </span>
                          <span className="flex-1 truncate">
                            {realtimeProviderShortLabel(row.id)}
                            {selectable ? '' : '（暂不可选）'}
                            <code className="ml-1 text-[11px] text-slate-400">{row.id}</code>
                          </span>
                          <button
                            type="button"
                            className="rounded p-1 text-slate-500 disabled:cursor-not-allowed disabled:opacity-30"
                            disabled={disabled || i === 0}
                            data-testid={`asr-realtime-move-up-${scenario}-${row.id}`}
                            aria-label="上移"
                            onClick={() => setEditor({ ...editor, rows: moveRealtimeRow(editor.rows, i, -1) })}
                          >
                            <ArrowUp className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            className="rounded p-1 text-slate-500 disabled:cursor-not-allowed disabled:opacity-30"
                            disabled={disabled || i === editor.rows.length - 1}
                            data-testid={`asr-realtime-move-down-${scenario}-${row.id}`}
                            aria-label="下移"
                            onClick={() => setEditor({ ...editor, rows: moveRealtimeRow(editor.rows, i, 1) })}
                          >
                            <ArrowDown className="h-4 w-4" />
                          </button>
                        </li>
                      );
                    })}
                  </ol>

                  <p className="mt-2 text-xs text-slate-500" data-testid={`asr-realtime-effective-${scenario}`}>
                    生效顺序：
                    <span className="font-mono">
                      {editor.inherit
                        ? (view.defaultRealtimeOrder ?? []).join(' → ') || '后端实时链'
                        : included.length > 0
                          ? included.join(' → ')
                          : '（至少勾选一个 provider）'}
                    </span>
                  </p>

                  {canEdit ? (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        className="rounded-lg bg-[#ff6a3d] px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
                        disabled={
                          !realtimeDirty[scenario] ||
                          anyPending ||
                          (!editor.inherit && included.length === 0)
                        }
                        data-testid={`asr-realtime-save-button-${scenario}`}
                        onClick={() => realtimeMutation.mutate({ scenario, editor })}
                      >
                        {saving ? '保存中…' : '保存兜底顺序'}
                      </button>
                      <button
                        type="button"
                        className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={anyPending || editor.inherit}
                        data-testid={`asr-realtime-restore-default-${scenario}`}
                        onClick={() => {
                          setEditor({ ...editor, inherit: true });
                          realtimeMutation.mutate({
                            scenario,
                            editor: { inherit: true, rows: editor.rows },
                          });
                        }}
                      >
                        恢复继承默认
                      </button>
                    </div>
                  ) : (
                    <p className="mt-2 text-xs text-slate-500">
                      当前角色仅可查看；调整实时兜底顺序需 ADMIN 或 SUPER_ADMIN。
                    </p>
                  )}
                </div>
              );
            })}
            {realtimeMutation.isError && (
              <p
                className="text-sm text-red-600 lg:col-span-2"
                data-testid="asr-realtime-save-error"
              >
                {realtimeMutation.error instanceof ApiError
                  ? realtimeMutation.error.message
                  : '保存失败'}
              </p>
            )}
            {realtimeNote && !realtimeDirty.sopRealtime && !realtimeDirty.qcRealtime && (
              <p className="text-sm text-emerald-600 lg:col-span-2">{realtimeNote}</p>
            )}
          </div>
        </>
      ) : null}

      <div className="mt-5 rounded-lg border border-slate-200 p-3">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
          场景语种 / 方言
        </div>
        <p className="mt-1 text-xs leading-relaxed text-slate-500">
          默认自动方言。上传录音文件 / URL 会优先走录音文件转写大模型；实时录音会优先走实时语音转写大模型。
        </p>
        <div className="mt-3 grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          {view.scenarios.map((s) => (
            <label key={s.scenario} className="text-xs font-medium text-slate-600">
              {SCENARIO_LABELS[s.scenario]}
              <select
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-400"
                value={languages[s.scenario] ?? 'auto'}
                disabled={!canEdit || languageMutation.isPending}
                data-testid={`asr-language-${s.scenario}`}
                onChange={(e) => {
                  setLanguages((prev) => ({ ...prev, [s.scenario]: e.target.value }));
                  setLanguageNote(null);
                }}
              >
                {(view.languageOptions ?? [{ value: 'auto', label: '自动方言（推荐）' }]).map(
                  (opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ),
                )}
              </select>
            </label>
          ))}
        </div>
        {canEdit ? (
          <button
            type="button"
            className="mt-3 rounded-lg bg-[#ff6a3d] px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
            disabled={!languageDirty || languageMutation.isPending}
            data-testid="asr-language-save-button"
            onClick={() => languageMutation.mutate(languages)}
          >
            {languageMutation.isPending ? '保存中…' : '保存语种设置'}
          </button>
        ) : (
          <p className="mt-2 text-xs text-slate-500">
            当前角色仅可查看；修改语种需 ADMIN 或 SUPER_ADMIN。
          </p>
        )}
        {languageMutation.isError && (
          <p className="mt-2 text-sm text-red-600" data-testid="asr-language-save-error">
            {languageMutation.error instanceof ApiError
              ? languageMutation.error.message
              : '保存失败'}
          </p>
        )}
        {languageNote && !languageDirty && (
          <p className="mt-2 text-sm text-emerald-600">{languageNote}</p>
        )}
      </div>

      <div className="mt-5">
        <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
          主用识别 Provider（录音文件 / 最终转写）
        </label>
        {/* Issue #367 — 明确该开关**仅**作用于录音文件 / 最终转写批量链（SOP 最终转写、质检最终转写），
            不是所有场景通用主用 Provider；实时语音转写请在上方按 SOP / 质检场景单独配置。 */}
        <p className="mt-1 text-xs leading-relaxed text-slate-500" data-testid="asr-primary-scope-note">
          仅影响录音文件 / 音频 URL 的最终转写链路（SOP 最终转写、质检最终转写）。实时语音转写不受此项
          影响，请在上方「实时语音转写」按场景单独配置。「继承部署默认」保存后即回到部署默认文件链。
        </p>
        <select
          className="mt-2 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-400"
          value={primary}
          disabled={!canEdit || mutation.isPending}
          data-testid="asr-primary-select"
          onChange={(e) => {
            setPrimary(e.target.value);
            setSavedNote(null);
          }}
        >
          <option value={INHERIT}>继承部署默认（录音文件链 ASR_PROVIDER_ORDER）</option>
          {view.providers.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
        {canEdit && (
          <button
            type="button"
            className="mt-3 rounded-lg bg-[#ff6a3d] px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
            disabled={!dirty || mutation.isPending}
            data-testid="asr-save-button"
            onClick={() => mutation.mutate(primary)}
          >
            {mutation.isPending ? '保存中…' : '保存切换'}
          </button>
        )}
        {!canEdit && (
          <p className="mt-2 text-xs text-slate-500">
            当前角色仅可查看；切换主用/备用 provider 需 ADMIN 或 SUPER_ADMIN。
          </p>
        )}
        {mutation.isError && (
          <p className="mt-2 text-sm text-red-600" data-testid="asr-save-error">
            {mutation.error instanceof ApiError ? mutation.error.message : '保存失败'}
          </p>
        )}
        {savedNote && !dirty && (
          <p className="mt-2 text-sm text-emerald-600">{savedNote}</p>
        )}
      </div>

      {/* Issue #367 — 各场景生效顺序按「录音文件 / 最终转写」与「实时语音转写」两条链路分区展示，
          让管理员一眼看清 SOP 最终转写 / 质检最终转写走文件链、SOP 实时 / 质检实时走实时链，
          source=default 即部署推荐默认。 */}
      <div className="mt-5">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
          各场景生效顺序
        </div>
        {(
          [
            {
              kind: 'file' as const,
              title: '录音文件 / 最终转写链',
              testid: 'asr-effective-group-file',
            },
            {
              kind: 'realtime' as const,
              title: '实时语音转写链',
              testid: 'asr-effective-group-realtime',
            },
          ]
        ).map((group) => {
          const rows = view.scenarios.filter((s) => scenarioKind(s) === group.kind);
          if (rows.length === 0) return null;
          return (
            <div key={group.kind} className="mt-2" data-testid={group.testid}>
              <div className="text-[11px] font-medium text-slate-400">{group.title}</div>
              <div className="mt-1 space-y-1.5">
                {rows.map((s) => (
                  <div
                    key={s.scenario}
                    className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-700"
                    data-testid={`asr-scenario-${s.scenario}`}
                  >
                    <span className="text-slate-600">
                      {SCENARIO_LABELS[s.scenario]}
                      {!s.routable && (
                        <span className="ml-1 text-xs text-slate-400">（讯飞流式，暂不随设置切换）</span>
                      )}
                    </span>
                    <span className="font-mono text-xs text-slate-500">
                      {s.effectiveOrder.join(' → ')}{' '}
                      <span className="text-slate-400">({s.source})</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <p className="mt-4 text-xs text-slate-500">
        优先级：管理员配置 &gt; 环境变量 ASR_PROVIDER_ORDER &gt; 默认链
        {' '}
        {view.defaultOrder.join(',')}。任一 provider 不可用会自动跳过并回退；全部不可用时
        转写任务报错而非返回假文本。切换会写入审计日志。
      </p>
    </Card>
  );
}
